"use client";

import { BlogCardReadMoreArrow } from "@/components/molecules/BlogCardReadMoreArrow";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { publicAxios } from "@/lib/axios";
import { Calendar } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type BlogCategory = {
  id: number;
  name: string;
  icon_url: string | null;
  background_image_url: string | null;
  /** When true, this chip means “all posts” — do not filter by category_id. */
  is_default_category?: boolean | number | string;
};

type BlogPost = {
  id: number;
  path: string;
  blog_banner_image: string | null;
  blog_title: string | null;
  blog_description: string | null;
  blog_category_ids?: number[] | null;
  created_at?: string | null;
};

const DESKTOP_PAGE = 6;
const MOBILE_PAGE = 3;
const DESKTOP_FETCH = 6;
const MOBILE_FETCH = 3;

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/** Figma category chip tint — solid brand color at 10% (node 7861:9981, 7700:10084). */
function hexToRgba(hex: string, alpha: number): string | null {
  const raw = hex?.trim().replace("#", "");
  if (!raw) return null;
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw.length === 6
        ? raw
        : null;
  if (!full) return null;
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return null;
  return `rgba(${r},${g},${b},${alpha})`;
}

function categoryActiveSurfaceTint(solid: string) {
  return hexToRgba(solid, 0.1) ?? "rgba(96,5,14,0.1)";
}

/** Vuesax bold arrow-circle (Figma 7861:9866, 7883:10646); same path as Faqs2 ArrowCircleDown, rotated for axis. */
const VUESAX_BOLD_ARROW_CIRCLE_FILLED =
  "M12 2C6.49 2 2 6.49 2 12C2 17.51 6.49 22 12 22C17.51 22 22 17.51 22 12C22 6.49 17.51 2 12 2ZM16.06 11.27L12.53 14.8C12.38 14.95 12.19 15.02 12 15.02C11.81 15.02 11.62 14.95 11.47 14.8L7.94 11.27C7.65 10.98 7.65 10.5 7.94 10.21C8.23 9.92 8.71 9.92 9 10.21L12 13.21L15 10.21C15.29 9.92 15.77 9.92 16.06 10.21C16.35 10.5 16.35 10.98 16.06 11.27Z";

function CategoryCarouselArrowCircle({
  direction,
  color,
  className,
}: {
  direction: "left" | "right";
  color: string;
  className?: string;
}) {
  const rotate = direction === "right" ? -90 : 90;
  return (
    <svg
      className={cn("size-10 shrink-0", className)}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ color }}
    >
      <g transform={`rotate(${rotate} 12 12)`}>
        <path d={VUESAX_BOLD_ARROW_CIRCLE_FILLED} fill="currentColor" />
      </g>
    </svg>
  );
}

function categoryChipActive(
  cat: BlogCategory,
  selectedId: number | null,
  defaultId: number | null
): boolean {
  if (selectedId != null) {
    return selectedId === cat.id;
  }
  if (defaultId != null) {
    return cat.id === defaultId;
  }
  return false;
}

function BlogCategoryChipItem({
  cat,
  active,
  onClick,
  labelColor,
  activeSolidColor,
}: {
  cat: BlogCategory;
  active: boolean;
  onClick: () => void;
  labelColor: string;
  activeSolidColor: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex min-h-0 min-w-0 flex-col items-center justify-center rounded-[18px] border-0 px-1 shadow-none outline-none transition-colors md:rounded-[30px]",
        "gap-2.5 py-1.5 md:gap-[18px] md:py-2.5",
        "w-[118px] shrink-0 md:w-full md:max-w-[160px]",
        "focus-visible:ring-2 focus-visible:ring-[#60050E]/40 focus-visible:ring-offset-2",
        "w-[100px] h-[94px] sm:w-[135px] sm:h-[152px]",
      )}
      style={{
        backgroundColor: active
          ? categoryActiveSurfaceTint(activeSolidColor)
          : undefined,
      }}
    >
      <div className="relative shrink-0 size-[50px] sm:size-[92px]">
        {cat.background_image_url ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={cat.background_image_url}
              alt=""
              className="absolute inset-0 size-full rounded-full object-cover w-[54px] h-[54px] sm:w-[92px] sm:h-[92px]"
            />
            <div
              className="absolute inset-0 rounded-full bg-black/25"
              aria-hidden
            />
          </>
        ) : (
          <div
            className="absolute inset-0 rounded-full bg-neutral-200 md:bg-neutral-100"
            aria-hidden
          />
        )}
        {cat.icon_url ? (
          <div className="absolute inset-0 flex items-center justify-center">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={cat.icon_url}
              alt=""
              className="w-[20px] h-[20px] sm:w-[32px] sm:h-[32px] object-contain md:size-5" />
          </div>
        ) : null}
      </div>
      <span
        className="max-w-[100px] whitespace-pre-wrap text-center text-[10px] font-semibold leading-snug tracking-[0.1px] md:max-w-[135px] md:text-[14px] md:leading-snug md:tracking-[0.14px]"
        style={{ color: labelColor }}
      >
        {cat.name}
      </span>
    </button>
  );
}

export default function BlogList1({
  data,
  isCmsPreview,
}: {
  data: any;
  isCmsPreview?: boolean;
}) {
  const ref = useRef<HTMLElement>(null);
  const sectionRoot = (data as any)?.data ?? data;

  const isMobileHook = useIsMobile(768);
  /** ExploreCourses1 / ProgramCards1: desktop unless viewport is below 768px (`null` → desktop until measured). */
  const isMdUp = isMobileHook !== true;
  const isMobileLayout = isMobileHook === true;

  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categoryDesktopPage, setCategoryDesktopPage] = useState(0);
  const mobileCategoryScrollRef = useRef<HTMLDivElement>(null);
  const [mobileCategoryFades, setMobileCategoryFades] = useState({
    left: false,
    right: false,
  });

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "blog_list_section");
  //   }
  // }, []);

  const {
    title = "Blogs",
    title_color = "#000000",
    description = "",
    description_color = "#555555",
    section_background_color = "#FFFFFF",
    read_more_label = "Read More",
    read_more_text_color = "#60050e",
    category_title_color = "#374151",
    category_active_background_color = "#BE2330",
    category_carousel_left_arrow_url = "",
    category_carousel_right_arrow_url = "",
    date_calendar_icon_url = "",
    date_text_color = "#373737",
    date_icon_color = "#373737",
    card_title_color = "#101112",
    card_description_color = "#101112",
    status = false,
    university_id = null,
    categories = [],
    blogs,
  } = sectionRoot || {};

  const categoryFadeLeftStyle = useMemo(
    () => ({
      background: `linear-gradient(to right, ${section_background_color}, transparent)`,
    }),
    [section_background_color]
  );
  const categoryFadeRightStyle = useMemo(
    () => ({
      background: `linear-gradient(to left, ${section_background_color}, transparent)`,
    }),
    [section_background_color]
  );

  const pageSize = isMdUp ? DESKTOP_PAGE : MOBILE_PAGE;
  const fetchSize = isMdUp ? DESKTOP_FETCH : MOBILE_FETCH;

  const categoriesList: BlogCategory[] = useMemo(
    () => (Array.isArray(categories) ? categories : []),
    [categories]
  );

  const defaultCategoryId = useMemo(() => {
    const row = categoriesList.find(
      (c) =>
        c.is_default_category === true ||
        c.is_default_category === 1 ||
        c.is_default_category === "1"
    );
    return row?.id ?? null;
  }, [categoriesList]);

  /** Omit category filter for “all posts” (no selection or default chip). */
  const categoryIdForQuery =
    selectedCategoryId != null &&
    (defaultCategoryId == null || selectedCategoryId !== defaultCategoryId)
      ? selectedCategoryId
      : undefined;

  const isActive =
    status === true || status === 1 || status === "1";

  useEffect(() => {
    if (!isMdUp) return;
    const n = categoriesList.length;
    if (n === 0) {
      setCategoryDesktopPage(0);
      return;
    }
    const numChunks = Math.max(1, Math.ceil(n / DESKTOP_PAGE));

    // "All posts" (no category filter): keep the current carousel page so unselecting
    // — e.g. the last chip — does not jump to the first chunk or the default chip's chunk.
    if (selectedCategoryId == null) {
      setCategoryDesktopPage((p) => Math.min(Math.max(0, p), numChunks - 1));
      return;
    }

    const targetIndex = categoriesList.findIndex(
      (c) => c.id === selectedCategoryId
    );
    if (targetIndex < 0) {
      setCategoryDesktopPage(0);
      return;
    }
    const page = Math.min(
      Math.floor(targetIndex / DESKTOP_PAGE),
      numChunks - 1
    );
    setCategoryDesktopPage(page);
  }, [selectedCategoryId, categoriesList, isMdUp]);

  useEffect(() => {
    if (isCmsPreview) {
      const seedItems = Array.isArray(blogs?.items) ? blogs.items : [];
      const displayItems =
        categoryIdForQuery == null
          ? seedItems
          : seedItems.filter(
              (p: BlogPost) =>
                Array.isArray(p.blog_category_ids) &&
                p.blog_category_ids.includes(categoryIdForQuery)
            );
      setPosts(displayItems);
      setTotal(
        categoryIdForQuery == null
          ? typeof blogs?.total === "number"
            ? blogs.total
            : seedItems.length
          : displayItems.length
      );
      return;
    }
    if (university_id == null) return;

    let cancelled = false;

    const load = async () => {
      if (
        categoryIdForQuery == null &&
        Array.isArray(blogs?.items) &&
        blogs.items.length > 0
      ) {
        setPosts(blogs.items);
        setTotal(
          typeof blogs?.total === "number" ? blogs.total : blogs.items.length
        );
        return;
      }

      setLoading(true);
      try {
        const res = await publicAxios.get("/page/blog-posts", {
          params: {
            university_id,
            category_id: categoryIdForQuery,
            offset: 0,
            limit: fetchSize,
          },
        });
        if (cancelled) return;
        const payload = res.data?.data ?? res.data;
        setPosts(Array.isArray(payload?.items) ? payload.items : []);
        setTotal(typeof payload?.total === "number" ? payload.total : 0);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    selectedCategoryId,
    defaultCategoryId,
    categoryIdForQuery,
    university_id,
    isCmsPreview,
    blogs,
    fetchSize,
  ]);

  const fetchMore = useCallback(
    async (offset: number, limit: number) => {
      if (university_id == null || isCmsPreview) {
        return { items: [] as BlogPost[], total: 0 };
      }
      setLoading(true);
      try {
        const res = await publicAxios.get("/page/blog-posts", {
          params: {
            university_id,
            category_id: categoryIdForQuery,
            offset,
            limit,
          },
        });
        const payload = res.data?.data ?? res.data;
        return {
          items: Array.isArray(payload?.items) ? payload.items : [],
          total: typeof payload?.total === "number" ? payload.total : 0,
        };
      } catch (e) {
        console.error(e);
        return { items: [] as BlogPost[], total: total };
      } finally {
        setLoading(false);
      }
    },
    [university_id, categoryIdForQuery, isCmsPreview, total]
  );

  const toggleCategory = (id: number) => {
    setSelectedCategoryId((prev) => (prev === id ? null : id));
  };

  const syncMobileCategoryScrollFades = useCallback(() => {
    const el = mobileCategoryScrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const eps = 2;
    setMobileCategoryFades({
      left: scrollLeft > eps,
      right: scrollLeft + clientWidth < scrollWidth - eps,
    });
  }, []);

  useLayoutEffect(() => {
    if (isMdUp) return;
    syncMobileCategoryScrollFades();
  }, [isMdUp, categoriesList.length, syncMobileCategoryScrollFades]);

  useEffect(() => {
    if (isMdUp) {
      setMobileCategoryFades({ left: false, right: false });
      return;
    }
    const el = mobileCategoryScrollRef.current;
    if (!el) return;
    const run = () => syncMobileCategoryScrollFades();
    run();
    el.addEventListener("scroll", run, { passive: true });
    const ro = new ResizeObserver(run);
    ro.observe(el);
    window.addEventListener("resize", run);
    return () => {
      el.removeEventListener("scroll", run);
      ro.disconnect();
      window.removeEventListener("resize", run);
    };
  }, [isMdUp, categoriesList.length, syncMobileCategoryScrollFades]);

  const categoryChunks = useMemo(
    () => chunk(categoriesList, DESKTOP_PAGE),
    [categoriesList]
  );

  const showCategoryArrows = isMdUp && categoriesList.length > DESKTOP_PAGE;

  const hrefForPost = (post: BlogPost): string => post.path || "/";

  const formatBlogCardDate = (iso: BlogPost["created_at"]): string => {
    if (iso == null || iso === "") return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const readMoreRow = (post: BlogPost) => {
    const href = hrefForPost(post);
    const trimmed = href.trim();
    const label = read_more_label || "Read More";
    const color = read_more_text_color || "#60050e";
    const arrowCls =
      "size-[12px] tab:size-[13px] md:size-[14px] lg:size-4 xl:size-[18px]";
    const rowCls = cn(
      "inline-flex items-center transition-opacity hover:opacity-90",
      "gap-[6px] tab:gap-2 md:gap-2 lg:gap-2.5 xl:gap-3",
    );
    const labelCls = cn(
      "text-center font-semibold leading-tight tracking-0 text-[14px]",
      "md:text-[16px]",
      "lg:text-[17px]",
      "xl:text-[18px]",
    );

    if (!trimmed) {
      return (
        <span className={rowCls} style={{ color }}>
          <span className={labelCls}>{label}</span>
          <BlogCardReadMoreArrow className={arrowCls} />
        </span>
      );
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return (
        <a
          href={trimmed}
          target="_blank"
          rel="noopener noreferrer"
          className={rowCls}
          style={{ color }}
        >
          <span className={labelCls}>{label}</span>
          <BlogCardReadMoreArrow className={arrowCls} />
        </a>
      );
    }
    const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return (
      <Link href={path} className={rowCls} style={{ color }}>
        <span className={labelCls}>{label}</span>
        <BlogCardReadMoreArrow className={arrowCls} />
      </Link>
    );
  };

  const [visibleCount, setVisibleCount] = useState(pageSize);

  useEffect(() => {
    setVisibleCount(pageSize);
  }, [pageSize, selectedCategoryId, blogs]);

  const visiblePosts = posts.slice(0, visibleCount);

  const showLoadMore = total > pageSize && visibleCount < total;

  const handleLoadMore = async () => {
    if (visibleCount < posts.length) {
      setVisibleCount((v) => Math.min(v + pageSize, posts.length));
      return;
    }
    if (posts.length >= total) return;
    const next = await fetchMore(posts.length, fetchSize);
    if (next.items.length) {
      setPosts((p) => [...p, ...next.items]);
      setTotal(next.total);
    }
    setVisibleCount((v) => v + pageSize);
  };

  if (!sectionRoot || !isActive) {
    return null;
  }

  if (!isCmsPreview && categoriesList.length === 0 && posts.length === 0) {
    return null;
  }

  const navLabelColor = category_title_color;
  const navActiveSolid = category_active_background_color;
  const leftCarouselArrow = String(
    category_carousel_left_arrow_url || ""
  ).trim();
  const rightCarouselArrow = String(
    category_carousel_right_arrow_url || ""
  ).trim();
  const customDateCalendarIcon = String(date_calendar_icon_url || "").trim();

  const blogPostArticles = visiblePosts.map((post) => {
    const dateStr = formatBlogCardDate(post.created_at);
    return (
      <article
        key={post.id}
        className={cn(
          "flex min-h-0 flex-col overflow-hidden rounded-lg border border-gray-3 bg-[#f8f9fa] transition-all duration-300 not-italic",
          "[font-family:var(--font-poppins),Poppins,sans-serif]",
          "shrink-0 tab:min-w-0 lg:min-w-0",
          "h-[470px] w-[307px] p-spacing-16",
          /* Desktop: 3 columns via grid on parent — width = (100% − 2×gap) / 3 */
          "tab:h-auto tab:w-full tab:min-h-[420px]",
          "md:min-h-[438px]",
          "lg:h-auto lg:min-h-[420px] lg:max-w-none",
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-spacing-12">
          <div
            className={cn(
              "relative mx-auto shrink-0 overflow-hidden rounded-lg",
              "h-[168px] w-[273px]",
              "tab:mx-0 tab:h-[138px] tab:w-full",
              "lg:aspect-[296/138] lg:h-auto lg:w-full",
            )}
          >
            {post.blog_banner_image ? (
              <Image
                src={post.blog_banner_image}
                alt=""
                fill
                className="object-cover"
                sizes="(max-width: 767px) 273px, (max-width: 1429px) 273px, min(574px, 33vw)"
              />
            ) : (
              <div
                className="absolute inset-0 bg-neutral-200"
                aria-hidden
              />
            )}
          </div>

          <div
            className={cn(
              "flex min-h-0 w-full flex-1 flex-col gap-spacing-8",
              "tab:gap-spacing-24",
            )}
          >
            <div className="flex w-full flex-col gap-spacing-8">
              {dateStr ? (
                <div className="flex items-center gap-2">
                  {customDateCalendarIcon ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={customDateCalendarIcon}
                      alt=""
                      className="h-[21px] w-[21px] shrink-0 object-contain tab:h-7 tab:w-7 md:h-[30px] md:w-[30px] lg:size-8 xl:size-[34px]"
                    />
                  ) : (
                    <Calendar
                      className="h-[21px] w-[21px] shrink-0 tab:size-7 md:size-[30px] lg:size-8 xl:size-[34px]"
                      strokeWidth={1.75}
                      aria-hidden
                      style={{ color: date_icon_color }}
                    />
                  )}
                  <span
                    className="whitespace-nowrap text-[13px] font-semibold leading-tight tracking-0 tab:text-[14px] md:text-[16px] lg:text-[17px] xl:text-[18px]"
                    style={{ color: date_text_color }}
                  >
                    {dateStr}
                  </span>
                </div>
              ) : null}
              <h3
                className={cn(
                  "line-clamp-3 w-full text-left text-[18px] font-bold leading-normal tracking-0",
                  "tab:text-[18px] tab:font-semibold",
                  "lg:text-[24px] lg:font-semibold",
                  "xl:text-[26px] xl:font-bold",
                )}
                style={{
                  color: card_title_color,
                }}
              >
                <Link href={post.path ?? ""}>
                  {post.blog_title || ""}
                </Link>
              </h3>
              <p
                className={cn(
                  /* line-clamp sets height from line count; fixed tab/md h-* was ~2 lines and hid the 3rd */
                  "line-clamp-4 tab:line-clamp-3 w-full text-[14px] font-normal leading-relaxed",
                  "md:text-[16px]",
                  "lg:text-[17px]",
                  "xl:text-[18px]",
                )}
                style={{
                  color: card_description_color,
                }}
              >
                {post.blog_description || ""}
              </p>
            </div>
            <div className="mt-auto flex w-full shrink-0 flex-col items-start">
              {readMoreRow(post)}
            </div>
          </div>
        </div>
      </article>
    );
  });

  return (
    <section
      ref={ref}
      className={cn(
        "flex w-full flex-col items-center justify-center gap-6 px-spacing-8 py-8 sm:gap-6 sm:px-[36px] sm:py-[42px] md:px-[44px] md:py-[52px] lg:px-[52px] lg:py-[60px] xl:px-[64px] xl:py-[72px]",
      )}
      style={{ backgroundColor: section_background_color || "#FFFFFF" }}
    >
      <div className="mx-auto flex w-full max-w-[1750px] flex-col gap-6">
        <h2
          className="text-left text-[26px] font-semibold leading-tight md:text-[28px] lg:text-[34px] xl:text-[42px]"
          style={{ color: title_color }}
        >
          {title}
        </h2>
        {description ? (
          <p
            className="mt-3 max-w-3xl text-base leading-relaxed md:text-[17px]"
            style={{ color: description_color }}
          >
            {description}
          </p>
        ) : null}

        {categoriesList.length > 0 ? (
          <div className="mt-8">
            {isMdUp ? (
              <div className="relative w-full">
                {showCategoryArrows ? (
                  <>
                    {categoryDesktopPage > 0 ? (
                      <div
                        className="pointer-events-none absolute inset-y-0 left-0 z-[1] w-[46px]"
                        style={categoryFadeLeftStyle}
                        aria-hidden
                      />
                    ) : null}
                    {categoryDesktopPage < categoryChunks.length - 1 ? (
                      <div
                        className="pointer-events-none absolute inset-y-0 right-0 z-[1] w-[46px]"
                        style={categoryFadeRightStyle}
                        aria-hidden
                      />
                    ) : null}
                    <button
                      type="button"
                      aria-label="Previous categories"
                      disabled={categoryDesktopPage <= 0}
                      onClick={() =>
                        setCategoryDesktopPage((p) => Math.max(0, p - 1))
                      }
                      className={cn(
                        "absolute left-1 top-1/2 z-[2] -translate-y-1/2 border-0 bg-transparent p-0 shadow-none outline-none transition-opacity focus-visible:ring-2 focus-visible:ring-[#60050E]/40 focus-visible:ring-offset-2 md:left-2",
                        categoryDesktopPage <= 0 &&
                          "cursor-not-allowed opacity-40"
                      )}
                    >
                      {leftCarouselArrow ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={leftCarouselArrow}
                          alt=""
                          className="block max-h-10 w-auto object-contain md:max-h-12"
                        />
                      ) : (
                        <CategoryCarouselArrowCircle
                          direction="left"
                          color={navActiveSolid}
                        />
                      )}
                    </button>
                    <button
                      type="button"
                      aria-label="Next categories"
                      disabled={
                        categoryDesktopPage >= categoryChunks.length - 1
                      }
                      onClick={() =>
                        setCategoryDesktopPage((p) =>
                          Math.min(categoryChunks.length - 1, p + 1)
                        )
                      }
                      className={cn(
                        "absolute right-1 top-1/2 z-[2] -translate-y-1/2 border-0 bg-transparent p-0 shadow-none outline-none transition-opacity focus-visible:ring-2 focus-visible:ring-[#60050E]/40 focus-visible:ring-offset-2 md:right-2",
                        categoryDesktopPage >= categoryChunks.length - 1 &&
                          "cursor-not-allowed opacity-40"
                      )}
                    >
                      {rightCarouselArrow ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={rightCarouselArrow}
                          alt=""
                          className="block max-h-10 w-auto object-contain md:max-h-12"
                        />
                      ) : (
                        <CategoryCarouselArrowCircle
                          direction="right"
                          color={navActiveSolid}
                        />
                      )}
                    </button>
                  </>
                ) : null}
                <div
                  className={cn(
                    "min-w-0 overflow-hidden",
                    showCategoryArrows && "px-11 md:px-14"
                  )}
                >
                  <div
                    className="flex transition-transform duration-300 ease-out"
                    style={{
                      transform: `translateX(-${categoryDesktopPage * 100}%)`,
                    }}
                  >
                    {categoryChunks.map((chunkCats, idx) => (
                      <div
                        key={idx}
                        className="grid min-w-full grid-cols-6 gap-4 md:gap-5"
                      >
                        {chunkCats.map((cat) => (
                          <BlogCategoryChipItem
                            key={cat.id}
                            cat={cat}
                            active={categoryChipActive(
                              cat,
                              selectedCategoryId,
                              defaultCategoryId
                            )}
                            onClick={() => toggleCategory(cat.id)}
                            labelColor={navLabelColor}
                            activeSolidColor={navActiveSolid}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative w-full">
                {mobileCategoryFades.left ? (
                  <div
                    className="pointer-events-none absolute inset-y-0 left-0 z-[1] w-[64px]"
                    style={categoryFadeLeftStyle}
                    aria-hidden
                  />
                ) : null}
                {mobileCategoryFades.right ? (
                  <div
                    className="pointer-events-none absolute inset-y-0 right-0 z-[1] w-[64px]"
                    style={categoryFadeRightStyle}
                    aria-hidden
                  />
                ) : null}
                <div
                  ref={mobileCategoryScrollRef}
                  className="-mx-1 flex snap-x snap-mandatory gap-[7px] sm:gap-[20px] overflow-x-auto scroll-px-4 pb-2"
                >
                  {categoriesList.map((cat) => (
                    <div key={cat.id} className="snap-start">
                      <BlogCategoryChipItem
                        cat={cat}
                        active={categoryChipActive(
                          cat,
                          selectedCategoryId,
                          defaultCategoryId
                        )}
                        onClick={() => toggleCategory(cat.id)}
                        labelColor={navLabelColor}
                        activeSolidColor={navActiveSolid}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}

        {isMobileLayout ? (
          <div className="mt-10 flex w-full flex-col items-center gap-y-[24px]">
            {blogPostArticles}
          </div>
        ) : (
          <div className="mx-auto w-full tab:grid tab:grid-cols-3 tab:justify-items-stretch tab:gap-x-[14px] tab:gap-y-[24px] md:gap-x-[18px] md:gap-y-[28px] lg:gap-x-[14px] lg:gap-y-[24px]">
            {blogPostArticles}
          </div>
        )}

        {showLoadMore ? (
          <div className="mt-10 flex justify-center">
            <button
              type="button"
              disabled={loading}
              onClick={handleLoadMore}
              className="rounded-lg px-8 py-3 text-[14px] font-semibold transition-opacity disabled:opacity-60"
              style={{
                backgroundColor: "#BE2330",
                color: "#FFFFFF",
              }}
            >
              {loading ? "Loading…" : "Load More"}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
